/** * 添付ファイル表示コンポーネント */
<template>
  <viewer
    v-bind:options="viewerOptions"
    v-bind:images="viewerImages"
    v-on:inited="$_initedViewer"
    class="attach-file-viewer"
  >
    <template v-slot="{ images }">
      <div class="attach-file-viewer-list">
        <div
          v-for="image in images"
          v-bind:key="image.source"
          class="attach-file-viewer-item"
          v-ifield-b-tooltip.hover="{
            ...tooltipOptions,
            title: image.filename,
            interactive: true,
          }"
        >
          <img
            class="image"
            v-bind:src="image.thumbnailUrl"
            v-bind:data-src="image.sourceUrl"
            v-bind:download-url="image.downloadUrl"
            v-bind:alt="image.filename"
          />
          <p class="name">{{ image.filename }}</p>
        </div>
      </div>
    </template>
  </viewer>
</template>

<script>
import { RestApiClient } from 'ifield-core-components'
import 'viewerjs/dist/viewer.css'
import { component as Viewer } from 'v-viewer'
import { useHandleApiError } from '@/composables/UseHandleApiError'
import ResponseUtil from '@/js/util/ResponseUtil.js'
import pdfImage from '@/assets/ifield/img_pdf.png'
import tiffImage from '@/assets/ifield/img_tiff.png'
import mp4Image from '@/assets/ifield/img_mp4.png'

/**
 * <img>で表示可能な拡張子(小文字)
 */
const FILE_EXTENSIONS_DISPLAYABLE_ON_IMG = new Set([
  'jpg',
  'jpeg',
  'gif',
  'png',
])

/**
 * v-viewerのツールバーボタンの設定の規定値
 */
const V_VIEWER_TOOL_BUTTON_DEFAULT = {
  show: true,
  size: 'large',
}

/**
 * 添付ファイル種類
 */
const ATTACH_FILE_TYPE = {
  REPORT: '5',
  PLACE: '8',
  USER: '9',
}

/**
 * 添付ファイル種類 - ドメイン名称 Map
 */
const TYPE_DOMAIN_MAP = new Map([
  [ATTACH_FILE_TYPE.REPORT, 'report'],
  [ATTACH_FILE_TYPE.PLACE, 'place'],
  [ATTACH_FILE_TYPE.USER, 'user'],
])

/**
 * 固定画像を表示する拡張子と画像のMap
 */
const FIXED_SOURCE_MAP = new Map([
  ['pdf', pdfImage],
  ['mp4', mp4Image],
  ['tiff', tiffImage],
])

/**
 * サムネイル取得時に元ファイルの名前が格納されているヘッダー名
 */
const HEADER_ORIGINAL_FILENAME = 'X-iField-original-filename'

export default {
  name: 'AppAttachFileViewer',
  components: { Viewer },
  props: {
    /**
     * 画像データ
     */
    attachFiles: {
      type: Array,
      default: () => [],
    },
    /**
     * カスタム属性が対象の場合はtrue
     */
    isAttribute: {
      type: Boolean,
      default: false,
    },
    /**
     * 表示画像の機能タイプ別のレコードID
     */
    recordId: {
      type: Number,
      default: null,
    },
  },
  setup() {
    const { handleApiError } = useHandleApiError()
    return { handleApiError }
  },
  data: function () {
    return {
      /**
       * URLを解決した添付ファイル情報
       * {
       *   sourceUrl, // viewerで表示する画像のURL
       *   downloadUrl, // ユーザーがダウンロードする時のリクエスト先URL
       *   thumbnailUrl, // サムネイル表示する画像のURL
       *   filename, // ファイル名
       *   origin, // 元 attachFile データ
       * }
       */
      resolvedAttachFiles: [],
      /**
       * 添付ファイル情報キャッシュ
       */
      cache: new Map(),
      /**
       * v-viewerインスタンス
       */
      vViewer: null,
      /**
       * viewerのオプション
       */
      viewerOptions: {
        url: 'data-src',
        inheritedAttributes: ['download-url'],
        title: [1, (image) => image.alt],
        focus: false,
        toolbar: {
          zoomIn: V_VIEWER_TOOL_BUTTON_DEFAULT,
          zoomOut: V_VIEWER_TOOL_BUTTON_DEFAULT,
          oneToOne: V_VIEWER_TOOL_BUTTON_DEFAULT,
          reset: V_VIEWER_TOOL_BUTTON_DEFAULT,
          prev: V_VIEWER_TOOL_BUTTON_DEFAULT,
          play: V_VIEWER_TOOL_BUTTON_DEFAULT,
          next: V_VIEWER_TOOL_BUTTON_DEFAULT,
          rotateLeft: V_VIEWER_TOOL_BUTTON_DEFAULT,
          rotateRight: V_VIEWER_TOOL_BUTTON_DEFAULT,
          download: {
            show: true,
            size: 'large',
            click: () => {
              const a = document.createElement('a')
              a.href = this.vViewer.image.getAttribute('download-url')
              a.download = this.vViewer.image.alt
              document.body.appendChild(a)
              a.click()
              document.body.removeChild(a)
            },
          },
        },
        className: 'attach-file-viewer-modal',
      },
      /**
       * ツールチップのオプション
       * @type {Object}
       */
      tooltipOptions: {
        delay: { show: 0, hide: 100 },
      },
    }
  },
  computed: {
    /**
     * viewerの画像一覧
     * @type {Array.<Object>}
     */
    viewerImages: function () {
      return this.resolvedAttachFiles.filter((f) => f)
    },
  },
  watch: {
    attachFiles: {
      handler: function () {
        this.resolvedAttachFiles = [] // 削除変更を反映するために一旦クリア
        this.$_resolveAttachFilesUrl()
      },
      immediate: true,
    },
  },
  mounted: function () {
    this.$log.debug('appAttachFileViewer mounted.')
  },
  methods: {
    /**
     * 添付ファイルのURLを解決する
     * - キャッシュにあればキャッシュを使用
     * - キャッシュに無ければ以下実行
     *   - サムネイル画像データ取得
     *   - ファイル名取得
     *   - キャッシュに格納
     */
    $_resolveAttachFilesUrl: function () {
      this.attachFiles.forEach(async (file, index) => {
        const key = this.$_toDownloadUrl(file)

        if (this.cache.has(key)) {
          this.resolvedAttachFiles[index] = this.cache.get(key)
        } else {
          let summary
          try {
            summary = await this.$_getFileSummary(file)
          } catch {
            return
          }
          const fileData = {
            sourceUrl: this.$_toSourceUrl(file),
            downloadUrl: key,
            thumbnailUrl: summary.thumbnail,
            filename: summary.filename,
            origin: file,
          }
          this.cache.set(key, fileData)
          this.resolvedAttachFiles[index] = fileData
        }

        // リアクティブ更新をトリガー
        this.resolvedAttachFiles = [...this.resolvedAttachFiles]
      })
    },

    /**
     * ファイル概要情報(サムネイル、ファイル名)を取得する
     * @param {Object} file 添付ファイル情報
     * @returns {Promise.<Object>} ファイル概要情報(thumbnail, filename)
     */
    $_getFileSummary: async function (file) {
      const extension = file.filename_extension?.toLowerCase()
      const isFixedSource = FIXED_SOURCE_MAP.has(extension)

      const url = this.$_toThumbnailUrl(file)
      let response
      try {
        response = await RestApiClient.executeGetApiInResponse(url)
      } catch (error) {
        this.handleApiError(error, null)
        throw error
      }

      const filename = ResponseUtil.getFilenameFromHeader(
        response,
        HEADER_ORIGINAL_FILENAME
      )
      const thumbnail = await new Promise((resolve, reject) => {
        // 固定画像ソースの場合はローカルの画像を使用
        if (isFixedSource) {
          resolve(FIXED_SOURCE_MAP.get(extension))
        }
        response.blob().then((blob) => {
          const reader = new FileReader()
          reader.onload = () => resolve(reader.result)
          reader.onerror = (error) => reject(error)
          reader.readAsDataURL(blob)
        })
      })

      return { thumbnail, filename }
    },

    /**
     * source用URLに変換
     * @param {Object} file 添付ファイル情報
     * @returns {string} ソース用URL
     */
    $_toSourceUrl: function (file) {
      if (this.isAttribute) {
        return this.$_toUrlAttribute(file)
      }
      const extension = file.filename_extension.toLowerCase()
      if (FILE_EXTENSIONS_DISPLAYABLE_ON_IMG.has(extension)) {
        return this.$_toUrlStandard(file)
      } else {
        return FIXED_SOURCE_MAP.get(extension)
      }
    },

    /**
     * download用URLに変換
     * @param {Object} file 添付ファイル情報
     * @returns {string} ダウンロード用URL
     */
    $_toDownloadUrl: function (file) {
      return this.isAttribute
        ? this.$_toUrlAttribute(file)
        : this.$_toUrlStandard(file)
    },

    /**
     * サムネイル用URLに変換
     * @param {Object} file 添付ファイル情報
     * @returns {string} ダウンロード用URL
     */
    $_toThumbnailUrl: function (file) {
      return this.isAttribute
        ? this.$_toUrlAttribute(file, true)
        : this.$_toUrlStandard(file, true)
    },

    /**
     * 属性項目のファイルURLに変換
     * @param {Object} file 添付ファイル情報
     * @param {boolean} [thumbnail=false] サムネイルの場合はtrue
     * @returns {string} URL
     */
    $_toUrlAttribute: function (file, thumbnail = false) {
      const domain = TYPE_DOMAIN_MAP.get(file.attach_file_type)
      const subPath = thumbnail ? 'file_column_thumbnail' : 'file_column'
      const url = RestApiClient.createApiUrl(
        `${domain}/${this.recordId}/${subPath}/${file.id}`
      )
      return url.toString()
    },

    /**
     * 通常添付ファイルURLに変換
     * @param {Object} file 添付ファイル情報
     * @param {boolean} [thumbnail=false] サムネイルの場合はtrue
     * @returns {string} URL
     */
    $_toUrlStandard: function (file, thumbnail = false) {
      const url = RestApiClient.createApiUrl('download_file')
      const params = url.searchParams
      if (this.recordId) {
        this.recordId && params.append('id', this.recordId)
      }
      if (file.attach_file_type) {
        params.append('attach_file_type', file.attach_file_type)
      }
      if (file.id) {
        params.append('attach_file_id', file.id)
      }
      if (thumbnail) {
        params.append('thumbnail', 1)
      }
      return url.toString()
    },

    /**
     * viewerの初期化が完了した時の処理
     */
    $_initedViewer: function (viewer) {
      this.vViewer = viewer
    },
  },
}
</script>

<style lang="scss" scoped>
.attach-file-viewer-list {
  $-grid-min-column-count: 3;
  $-grid-gap: 8px;
  $-grid-item-min-width: 100px;

  // アイテムを3つ並べる時の最大幅を算出
  $-gap-count: calc(#{$-grid-min-column-count} - 1);
  $-total-gap-width: calc(#{$-grid-gap} * #{$-gap-count});
  $-grid-item-max-width: calc(
    (100% - #{$-total-gap-width}) / #{$-grid-min-column-count}
  );

  display: grid;
  grid-template-columns: repeat(
    auto-fit,
    minmax($-grid-item-min-width, min(#{$-grid-item-max-width}, 150px))
  );
  width: 100%;
  height: auto;
  list-style-type: none;
  gap: $-grid-gap;
}

.attach-file-viewer-item {
  width: 100%;
  height: calc(100px + 1.25rem);
  pointer-events: none;
  border: #ced4da solid 1px;
  cursor: pointer;
  pointer-events: auto;
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  align-items: center;

  .image {
    height: 100px;
    width: 100px;
    object-fit: cover;
  }

  .name {
    height: 1.25rem;
    width: 100%;
    margin: auto;
    padding: 4px 6px;
    font-size: $font-size-small;
    line-height: 1;
    overflow: hidden;
    white-space: nowrap;
    text-overflow: ellipsis;
    background-color: #ced4da;
  }

  &:hover {
    border: #b1b8bf solid 1px;
    .image {
      opacity: 0.7;
      transition-duration: 0.25s;
    }
  }
}
</style>

<style lang="scss">
.attach-file-viewer-modal {
  .viewer-title {
    line-height: 1.5;
    font-size: $font-size-large;
    color: #ffffff;
  }

  .viewer-download {
    &::before {
      content: '';
      width: 20px;
      height: 20px;
      background-image: url('../assets/ifield/icon_viewer_download.png');
      background-position: center;
      background-size: 14px;
      background-repeat: no-repeat;
      display: block;
    }
  }
}
</style>
